<template>
  <div 
    ref="el"
    :class="[
      `f-select-text`,
      `f-select-text--${size}`, 
      {
        'is-disabled': disabled,
        'is-focused': focus,
        'is-up': direction === 'up',
      }
    ]"
  >
    <p class="f-select-text__text">
      <slot name="prepend"></slot>
    </p>

    <div 
      class="f-select-text__input"
    >
      <p @click="toggle">
        <span class="f-select-text__placeholder">
          <slot 
            name="label"
            :label="labelHeader"
          >
            {{ labelHeader }}  
          </slot>
        </span>
      </p>

      <ul
        class="f-select-text__options"
      >
        <li
          v-for="option in options"
          :key="getId(option)"
          :class="[
            'f-select-text__option',
            {
              'is-active': isSelected(option)
            }
          ]"
          @click="select(option)"
        >
          <slot 
            name="option"
            :option="option"
            :selected="isSelected(option)"
          >
            <p>{{ getLabel(option) }}</p>
          </slot>
        </li>
      </ul>
    </div>

    <p class="f-select-text__text">
      <slot name="append"></slot>
    </p>
  </div>
</template>

<script>
  import { nextTick } from 'vue'
  import useSelect from '@composables/use-select'
  import useClickOutside from '@composables/use-click-outside'

  export default {
    props: {
      modelValue: {
        type: [Object, String, Number, Array, Boolean],
        required: true,
      },

      options: {
        type: Array,
        default: () => [], 
      },

      size: {
        type: String,
        default: 'normal',
      },

      placeholder: {
        type: String,
        default: '',
      },

      disabled: {
        type: Boolean,
        default: false,
      },

      id: {
        type: String,
        default: '',
      },

      value: {
        type: String,
        default: '',
      },

      label: {
        type: String,
        default: '',
      },

      direction: {
        type: String,
        default: 'down',
      }
    },

    setup (props, context) {
      const selection = useSelect(props.modelValue, {
        placeholder: props.placeholder,
        id: props.id,
        value: props.value,
        label: props.label,
        
        onChange (newValue) {
          context.emit('update:modelValue', newValue)
        },

        onOpen () {
          nextTick(() => {
            bindClickOutside()
          })
        },

        onClose () {
          nextTick(() => {
            unbindClickOutside()
          })
        },
      })

      const { el, bindClickOutside, unbindClickOutside } = useClickOutside(selection.close)


      return {
        el,
        ...selection,
      }
    }
  }
</script>

<style lang="scss">
  .f-select-text {
    position: relative;
    display: inline-flex;
    align-items: center;

    &.is-focused {
      .f-select-text__options {
        display: block;
        max-height: 2000px;
        opacity: 1;
      }

      .f-select-text__input {
        .f-select-text__placeholder {
          // color: $color-placeholder;

          &:after {
            background-color: $color-placeholder;
          }
        }
      }
    }

    &.is-up {
      .f-select-text__options {
        top: 0px;
        transform: translate3d(0, -100%, 0);
      }
    }

    .f-select-text__text {
      margin: 0px 3px;
    }

    .f-select-text__placeholder {
      position: relative;
      display: inline-block;
      color: $color-text;
      cursor: pointer;

      &:after {
        content: "";
        position: absolute;
        bottom: -2px;
        left: 0px;
        height: 1px;
        width: 100%;
        background-color: $color-text;
      }
    }

    .f-select-text__options div,
    .f-select-text__header {
      border: none;
      cursor: pointer;
    }

    .f-select-text__options {
      position: absolute;
      background-color: $white;
      top: 100%;
      left: 0px;
      max-height: 0px;
      width: 100%;
      overflow: hidden;
      z-index: 99;
      opacity: 0;
      transition: opacity 0.3s ease 0.1s, max-height 0.3s $magic-animation 0.1s;

      .f-select-text__option {
        position: relative;
        display: flex;
        align-items: center;
        height: 2rem;
        width: 100%;
        background: none;
        border: none;
        outline: none;
        text-align: left;
        cursor: pointer;

        & > * {
          width: 100%;
        }

        &:focus, &:active {
          outline: none;
          background-color: $color-disabled;
        }

        &:hover, &.is-selected {
          background-color: rgba(0, 0, 0, 0.1);
        }
      }
    }

    &.f-select-text--small {
      max-width: 50px;

      .f-select-text__header {
        padding: 0.5rem 0.2rem;
      }
    }

    &.f-select-text--normal {
      max-width: 150px;

      .f-select-text__header {
        padding: 0.8rem 0.5rem;
      }
    }

    &.f-select-text--medium {
      max-width: 150px;

      .f-select-text__header {
        padding: 0.9rem 0.7rem;
      }
    }

    &.f-select-text--large {
      max-width: 500px;

      .f-select-text__header {
        padding: 1rem 1rem;
      }
    }

    &.is-long {
      max-width: 100%;
    }
  }
</style>